import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Radio, X, Send } from "lucide-react";
import { opsAPI, PRIORITY_STYLE, timeAgo, cls } from "./opsApi";

export default function OpsBroadcasts() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = () => opsAPI.broadcasts().then(setRows);
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="ops-broadcasts">
      <div className="flex items-center gap-3">
        <div className="text-[13px] text-slate-500">
          Send targeted broadcasts to a role, department, or the whole hotel. Recipients receive an in-app notification instantly.
        </div>
        <button onClick={()=>setCreating(true)} data-testid="ops-bc-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> New Broadcast
        </button>
      </div>

      {rows.length === 0
        ? <div className="p-12 bg-white border border-slate-200 rounded-2xl text-center">
            <Radio className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
            <div className="text-[13px] text-slate-500">No broadcasts yet.</div>
          </div>
        : <div className="space-y-3">
            {rows.map(b => (
              <div key={b.id} data-testid={`ops-bc-${b.id}`}
                   className="bg-white border border-slate-200 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-hocp-navy text-white flex items-center justify-center shrink-0">
                    <Radio className="w-4 h-4"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-[14px] font-semibold text-hocp-navy">{b.subject || "(No subject)"}</div>
                      <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-medium capitalize ${PRIORITY_STYLE[b.priority]}`}>{b.priority}</span>
                    </div>
                    <div className="text-[13px] text-slate-700 mt-1 whitespace-pre-wrap">{b.body}</div>
                    <div className="text-[11px] mono text-slate-400 mt-2">
                      by {b.sender_email} · {timeAgo(b.created_at)}
                      {b.mentions?.length ? ` · @${b.mentions.length} mentioned` : ""}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>}

      {creating && <NewBroadcast onClose={()=>setCreating(false)} onDone={()=>{ setCreating(false); load(); }}/>}
    </div>
  );
}

function NewBroadcast({ onClose, onDone }) {
  const [f, setF] = useState({
    subject: "", body: "", priority:"normal",
    audience: { all: true }, mentions: [],
  });
  const [target, setTarget] = useState("all");   // all|role|department
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.body.trim()) { toast.error("Body required"); return; }
    let audience = { all: true };
    if (target === "role" && ref) audience = { role: ref };
    if (target === "department" && ref) audience = { department_id: ref };
    setSaving(true);
    try {
      await opsAPI.createBc({ ...f, audience });
      toast.success("Broadcast sent"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">New Broadcast</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Subject <span className="text-slate-400 font-normal">(optional)</span></span>
            <input value={f.subject} onChange={e=>setF({...f, subject: e.target.value})} placeholder="e.g. Fire drill at 3 PM"
                   data-testid="ops-bc-subject" className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Message</span>
            <textarea value={f.body} onChange={e=>setF({...f, body: e.target.value})} rows={5}
                       placeholder="Your message…" data-testid="ops-bc-body"
                       className={cls + " w-full h-auto py-2 resize-y"}/></label>
          <div className="grid grid-cols-3 gap-2">
            <select value={target} onChange={e=>setTarget(e.target.value)} data-testid="ops-bc-target"
                    className={cls}>
              <option value="all">Whole Hotel</option>
              <option value="role">Role</option>
              <option value="department">Department</option>
            </select>
            {target !== "all" && (
              <input value={ref} onChange={e=>setRef(e.target.value)}
                     placeholder={target === "role" ? "role code (e.g. gm)" : "department id"}
                     data-testid="ops-bc-ref" className={cls}/>
            )}
            <select value={f.priority} onChange={e=>setF({...f, priority: e.target.value})}
                    data-testid="ops-bc-priority" className={cls}>
              {["low","normal","high","critical"].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="px-5 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-100">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="ops-bc-submit"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
            <Send className="w-4 h-4"/> {saving ? "Sending…" : "Send Broadcast"}
          </button>
        </div>
      </div>
    </div>
  );
}
